import React, { useState, useMemo, useEffect } from 'react';
import { 
  Newspaper, 
  Tv, 
  Mic, 
  FileText, 
  ExternalLink, 
  Search, 
  Calendar, 
  Share2, 
  Filter, 
  ArrowUpRight, 
  PlayCircle,
  MessageSquare,
  Sparkles,
  Heart,
  ZoomIn,
  ZoomOut,
  X,
  Eye
} from 'lucide-react';
import { siteData } from '../data.js';
import SEO from '../components/SEO.jsx';
import { useCmsCollection } from '../lib/cms.js';

export default function TfdiPaaNettet() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);

  // Close modal on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Categories list with their corresponding icons and colors
  const categories = [
    { name: 'Alle', icon: Filter, color: 'bg-slate-100 text-slate-700 border-slate-200' },
    { name: 'Artikler & Nyheder', icon: Newspaper, color: 'bg-red-50 text-red-600 border-red-100' },
    { name: 'Video & TV', icon: Tv, color: 'bg-blue-50 text-blue-600 border-blue-100' },
    { name: 'Podcast & Webinarer', icon: Mic, color: 'bg-purple-50 text-purple-600 border-purple-100' },
    { name: 'Dokumenter & Breve', icon: FileText, color: 'bg-emerald-50 text-emerald-600 border-emerald-100' }
  ];

  // Helper to get icon for a specific category
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Artikler & Nyheder': return <Newspaper className="w-5 h-5" />;
      case 'Video & TV': return <Tv className="w-5 h-5" />;
      case 'Podcast & Webinarer': return <Mic className="w-5 h-5" />;
      case 'Dokumenter & Breve': return <FileText className="w-5 h-5" />;
      default: return <Newspaper className="w-5 h-5" />;
    }
  };

  // Helper to get category color classes
  const getCategoryColor = (category) => {
    switch (category) {
      case 'Artikler & Nyheder': return 'text-red-600 bg-red-50 border-red-100';
      case 'Video & TV': return 'text-blue-600 bg-blue-50 border-blue-100';
      case 'Podcast & Webinarer': return 'text-purple-600 bg-purple-50 border-purple-100';
      case 'Dokumenter & Breve': return 'text-emerald-600 bg-emerald-50 border-emerald-100';
      default: return 'text-slate-600 bg-slate-50 border-slate-100';
    }
  };

  const { data: rawWebItems } = useCmsCollection('tfdi_paa_nettet', siteData.onTheWeb || []);

  const onTheWebItems = useMemo(() => {
    return rawWebItems.map((item, idx) => {
      const d = item.data || item;
      return {
        id: d.id || item.id || `web-item-${idx}`,
        title: d.titel || d.title,
        titleLines: d.titleLines || (d.titel ? [d.titel] : []),
        source: d.kilde || d.source,
        date: d.dato || d.date,
        category: d.kategori_navn || d.category || 'Artikler & Nyheder',
        description: d.beskrivelse || d.description,
        linkText: d.link_tekst || d.linkText,
        url: d.link_url || d.url,
        image: Array.isArray(d.billede) ? d.billede[0] : (d.billede || d.image),
        imageAlt: d.billede_alt || d.imageAlt || d.titel || d.title,
        hasNoLink: d.har_intet_link !== undefined ? d.har_intet_link : Boolean(d.hasNoLink),
      };
    });
  }, [rawWebItems]);

  // Highlighted / Featured item (e.g. the TV2 Nord feature with Steffen)
  const featuredItem = useMemo(() => {
    return onTheWebItems.find(item => item.id === 'tv2nord-hemmelige-missioner-2025') || onTheWebItems[0];
  }, [onTheWebItems]);

  // Filter and search logic
  const filteredItems = useMemo(() => {
    return onTheWebItems.filter(item => {
      const matchesCategory = selectedCategory === 'Alle' || item.category === selectedCategory;
      const titleSearch = item.titleLines ? item.titleLines.join(' ') : item.title;
      const matchesSearch = (titleSearch && titleSearch.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (item.source && item.source.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (item.date && item.date.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [onTheWebItems, searchTerm, selectedCategory]);

  const handleShare = (item) => {
    const shareUrl = item.url || window.location.href;
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: item.description,
        url: shareUrl,
      }).catch(console.error);
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      alert('Linket er kopieret til udklipsholderen!');
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <SEO
        title="TFDI på nettet | Medieomtale, artikler og indslag"
        description="Følg Tak For Din Indsats i medierne. Se artikler, interviews, tv-indslag og podcasts om vores arbejde for Danmarks veteraner og frontpersonale."
      />
      {/* Hero Section */}
      <div className="relative bg-slate-900 text-white py-20 md:py-28 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.15),transparent_50%)]" />
        <div className="absolute -left-10 -bottom-10 w-96 h-96 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6">
            TFDI på <span className="text-red-500">nettet</span>
          </h1>
          <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Vi arbejder aktivt i medierne, det politiske landskab og den offentlige debat for at sikre, at Danmarks frontpersonale og deres pårørende får den anerkendelse, støtte og helhedsorienterede behandling, de fortjener.
          </p>
        </div>
      </div>

      {/* Featured Highlight Section */}
      {featuredItem && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20">
          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/80 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              {/* Left Side: Visual Accent */}
              <div className="lg:col-span-5 bg-slate-900 p-8 md:p-12 flex flex-col justify-between relative overflow-hidden text-white">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(220,38,38,0.2),transparent_60%)]" />
                <div className="relative z-10">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-600 text-white uppercase tracking-wider mb-6">
                    <Heart className="w-3 h-3 fill-current" /> Fremhævet indslag
                  </span>
                  <h3 className="text-2xl md:text-3xl font-black tracking-tight mb-4 leading-tight">
                    {featuredItem.title}
                  </h3>
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-8">
                    {featuredItem.description}
                  </p>
                </div>
                <div className="relative z-10 flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-red-500" /> {featuredItem.date}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
                    {featuredItem.source}
                  </span>
                </div>
              </div>

              {/* Right Side: Action & Link Details */}
              <div className="lg:col-span-7 p-8 md:p-12 flex flex-col justify-center bg-white">
                <div className="space-y-6">
                  <div className="flex items-center gap-3 text-red-600 font-bold text-sm uppercase tracking-wider">
                    <PlayCircle className="w-6 h-6" />
                    <span>Se eller læs indslaget</span>
                  </div>
                  <h4 className="text-xl font-bold text-slate-900">
                    {featuredItem.linkText}
                  </h4>
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                    Dette indslag giver et unikt indblik i vores arbejde og de personlige historier, der driver foreningen fremad. Klik på knappen nedenfor for at gå direkte til kilden.
                  </p>
                  <div className="flex flex-wrap gap-4 pt-4">
                    <a
                      href={featuredItem.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-600/20"
                    >
                      <span>Gå til indslag</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => handleShare(featuredItem)}
                      className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all duration-300"
                    >
                      <Share2 className="w-4 h-4" />
                      <span>Del link</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters & Search Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-6 justify-between items-start lg:items-center mb-10">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 w-full lg:w-auto">
            {categories.map((cat) => {
              const IconComponent = cat.icon;
              const isActive = selectedCategory === cat.name;
              return (
                <button
                  key={cat.name}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs md:text-sm font-bold border transition-all duration-300 cursor-pointer ${
                    isActive 
                      ? 'bg-slate-900 text-white border-slate-900 shadow-md shadow-slate-900/10' 
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                  }`}
                >
                  <IconComponent className="w-4 h-4 shrink-0" />
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>

          {/* Search Input */}
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Søg i indslag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-sm shadow-sm"
            />
          </div>
        </div>

        {/* Media Grid */}
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <div 
                key={item.id}
                className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/40 p-6 md:p-8 flex flex-col justify-between hover:shadow-2xl hover:shadow-slate-200/60 transition-all duration-300 group"
              >
                <div>
                  {/* Card Header: Category & Date */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${getCategoryColor(item.category)}`}>
                      {getCategoryIcon(item.category)}
                      <span>{item.category}</span>
                    </span>
                    <span className="text-xs font-semibold text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {item.date}
                    </span>
                  </div>

                  {/* Card Title (with 2 lines support) */}
                  {item.titleLines ? (
                    <div className="mb-4">
                      <h3 className="text-lg md:text-xl font-black text-slate-900 group-hover:text-red-600 transition-colors leading-snug">
                        {item.titleLines[0]}
                      </h3>
                      <p className="text-sm md:text-base font-bold text-slate-600 mt-1">
                        {item.titleLines[1]}
                      </p>
                    </div>
                  ) : (
                    <h3 className="text-lg md:text-xl font-black text-slate-900 mb-3 group-hover:text-red-600 transition-colors leading-snug">
                      {item.title}
                    </h3>
                  )}

                  {/* Attached Image (if available) */}
                  {item.image && (
                    <div 
                      onClick={() => {
                        setIsZoomed(false);
                        setSelectedImage({ 
                          url: item.image, 
                          title: item.titleLines ? item.titleLines.join(' – ') : item.title 
                        });
                      }}
                      className="mb-5 rounded-2xl overflow-hidden border border-slate-200 bg-white cursor-pointer group/img relative shadow-sm hover:shadow-md transition-all max-w-[260px] mx-auto"
                    >
                      <div className="bg-slate-50 p-2 flex items-center justify-center">
                        <img 
                          src={item.image} 
                          alt={item.imageAlt || item.title}
                          className="w-full h-auto max-h-[220px] object-contain object-top rounded-lg transition-transform duration-300 group-hover/img:scale-[1.02]"
                          loading="eager"
                        />
                      </div>
                      <div className="bg-slate-900/90 text-white text-[11px] font-semibold py-1.5 px-2.5 flex items-center justify-center gap-1.5 group-hover/img:bg-red-600 transition-colors">
                        <ZoomIn className="w-3 h-3" />
                        <span>Se i fuld størrelse</span>
                      </div>
                    </div>
                  )}

                  {/* Card Description */}
                  {item.description && (
                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                      {item.description}
                    </p>
                  )}

                  {/* Source Badge */}
                  <div className="mb-6">
                    <span className="text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-100 px-2.5 py-1 rounded-lg">
                      Kilde: {item.source}
                    </span>
                  </div>
                </div>

                {/* Card Footer: Action Links */}
                <div className="pt-5 border-t border-slate-50 flex items-center justify-between gap-4">
                  {item.hasNoLink || !item.url ? (
                    item.image ? (
                      <button
                        type="button"
                        onClick={() => setSelectedImage({ url: item.image, title: item.titleLines ? item.titleLines.join(' – ') : item.title })}
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700 transition-colors cursor-pointer"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Se vedhæftet opslag</span>
                      </button>
                    ) : (
                      <span className="text-xs font-medium text-slate-400">Kun til gennemsyn</span>
                    )
                  ) : (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-900 hover:text-red-600 transition-colors group/link"
                    >
                      <span>Læs / se mere</span>
                      <ExternalLink className="w-4 h-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                    </a>
                  )}
                  <button
                    onClick={() => handleShare(item)}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-all cursor-pointer"
                    title="Del indslag"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-xl p-12 text-center max-w-lg mx-auto">
            <div className="w-16 h-16 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Ingen indslag fundet</h3>
            <p className="text-slate-500 text-sm">
              Vi kunne desværre ikke finde nogen medieindslag, der matcher din søgning eller filtrering. Prøv at ændre dine søgeord eller vælg en anden kategori.
            </p>
          </div>
        )}
      </div>

      {/* Call to Action: Share Our Message */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="bg-slate-900 text-white rounded-[2.5rem] p-8 md:p-12 shadow-xl relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600/5 rounded-full blur-3xl -ml-20 -mb-20"></div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 text-red-500 font-bold text-sm uppercase tracking-wider mb-4">
                <MessageSquare size={18} />
                <span>Hjælp os med at sprede ordet</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-black mb-4 tracking-tight">
                Vær med til at sætte fokus på vores helte
              </h2>
              <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                Når du deler vores artikler, podcasts og videoer, er du med til at bryde tabuet om PTSD og psykiske belastninger blandt frontpersonale. Du hjælper os med at nå ud til politikere, beslutningstagere og ikke mindst de ramte og pårørende, der sidder derude og føler sig alene. Hver deling gør en reel forskel.
              </p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 flex flex-col gap-4">
              <p className="text-xs text-slate-300 leading-relaxed">
                Følg os på vores sociale medier, hvor vi løbende deler nye opdateringer, historier og resultater fra vores arbejde.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <a 
                  href={siteData.socials.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center py-2.5 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-white transition-colors border border-slate-700"
                >
                  Facebook
                </a>
                <a 
                  href={siteData.socials.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center py-2.5 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-white transition-colors border border-slate-700"
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 md:p-6"
          onClick={() => {
            setSelectedImage(null);
            setIsZoomed(false);
          }}
        >
          <div 
            className="relative max-w-4xl w-full max-h-[96vh] bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3.5 sm:p-5 border-b border-slate-200 bg-slate-50">
              <div className="pr-3 flex-1 min-w-0">
                <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-0.5">Vedhæftet opslag</p>
                <h4 className="font-black text-slate-900 text-sm sm:text-base truncate">
                  {selectedImage.title}
                </h4>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  onClick={() => setIsZoomed(!isZoomed)}
                  className="px-2.5 sm:px-3 py-1.5 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
                  title={isZoomed ? "Tilpas skærm" : "Zoom ind (100% skarphed)"}
                >
                  {isZoomed ? (
                    <>
                      <ZoomOut className="w-3.5 h-3.5 text-slate-500" />
                      <span className="hidden sm:inline">Tilpas skærm</span>
                    </>
                  ) : (
                    <>
                      <ZoomIn className="w-3.5 h-3.5 text-slate-500" />
                      <span className="hidden sm:inline">Zoom ind (100%)</span>
                    </>
                  )}
                </button>

                <a
                  href={selectedImage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2.5 sm:px-3 py-1.5 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
                  title="Åbn billede i fuld opløsning i ny fane"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
                  <span className="hidden sm:inline">Åbn original</span>
                </a>

                <button
                  onClick={() => {
                    setSelectedImage(null);
                    setIsZoomed(false);
                  }}
                  className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-200 rounded-lg sm:rounded-full transition-colors cursor-pointer"
                  aria-label="Luk visning"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body: High Sharpness View */}
            <div className="p-3 sm:p-6 overflow-auto max-h-[calc(96vh-72px)] flex items-start justify-center bg-slate-900/5">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className={`transition-all duration-200 rounded-lg shadow-md border border-slate-200/80 ${
                  isZoomed 
                    ? "w-auto max-w-none cursor-zoom-out" 
                    : "w-auto max-w-full max-h-[82vh] object-contain cursor-zoom-in"
                }`}
                onClick={() => setIsZoomed(!isZoomed)}
                title={isZoomed ? "Klik for at tilpasse skærm" : "Klik for at forstørre teksten"}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
